import axios, {
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosRequestTransformer,
  AxiosResponse,
  AxiosResponseTransformer,
} from "axios";

import camelcaseKeys from "camelcase-keys";
import snakecaseKeys from "snakecase-keys";

import {
  getAccessToken,
  removeAccessAndRefreshTokens,
} from "../lib/general/token";
import { ServiceStatusCodes } from "../types/common";
import { capitalizedString } from "../utils/common";
import toast from "react-hot-toast";

interface APIServiceOptions {
  withAuthToken?: boolean;
  params?: AxiosRequestConfig["params"];
  timeout?: AxiosRequestConfig["timeout"];
  formData?: boolean;
  /** Preserve user-provided/dynamic object keys, such as spreadsheet headers. */
  preservePayloadKeys?: boolean;
}

export interface APIListingResponse<T> {
  total: number;
  page: number;
  size: number;
  pages?: number;
  items: T[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformBackendErrorToFrontendError = (errorResponse: any) => ({
  ...errorResponse,
  fieldErrors:
    errorResponse.detail instanceof Array
      ? errorResponse.detail
          .filter((err: any) => err.loc.includes("body"))
          .reduce((acc: any, err: any) => {
            const key: string = Object.keys(
              camelcaseKeys({ [err.loc[err.loc.length - 1]]: true }),
            )[0];

            return {
              ...acc,
              [key]: {
                ...err,
                msg: capitalizedString(err.msg),
              },
            };
          }, {})
      : undefined,
});

// Logout function to handle user logout
const handleUserLogout = () => {
  removeAccessAndRefreshTokens();
  if (typeof window !== "undefined") {
    window.location.replace(
      `${APIService.domainUrl}/login?reason=session-expired`,
    );
  }
};

export class APIService {
  static API_PREFIX_URL: string =
    // "http://localhost:8000";
    // ||

    "https://tig-apis-production-1098968310049.asia-south1.run.app";
  static apiUrl = APIService.API_PREFIX_URL;
  static domainUrl = "https://app.theimmigrationgurus.ai/login";
  /** Sent on every request once a branch has been selected in the portal. */
  static activeLocationId: string | null = null;

  static setActiveLocation(locationId: string | null) {
    this.activeLocationId = locationId;
  }

  private static addLocationHeader(headers: AxiosRequestHeaders) {
    if (this.activeLocationId) {
      headers["X-Location-ID"] = this.activeLocationId;
    }
  }
  // "https://app.theimmigrationgurus.ai/login";

  static configure({
    apiUrl,
    domainUrl,
  }: {
    apiUrl?: string;
    domainUrl?: string;
  }) {
    if (apiUrl) {
      this.apiUrl = apiUrl;
      this.API_PREFIX_URL = apiUrl;
    }
    if (domainUrl) this.domainUrl = domainUrl;
    this.APIClient.defaults.baseURL = this.apiUrl;
  }

  static APIClient = axios.create({
    baseURL: APIService.API_PREFIX_URL,
    transformResponse: [
      ...(axios.defaults.transformResponse as AxiosResponseTransformer[]),
      (resBody) => (resBody ? camelcaseKeys(resBody, { deep: true }) : resBody),
      (data, _, status) =>
        status &&
        [ServiceStatusCodes.HTTP_422, ServiceStatusCodes.HTTP_409].includes(
          status,
        )
          ? transformBackendErrorToFrontendError(data)
          : data,
    ],
    transformRequest: [
      (reqBody) => (reqBody ? snakecaseKeys(reqBody, { deep: true }) : reqBody),
      ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
    ],
  });

  // Response interceptor to handle 401 unauthorized responses
  static {
    this.APIClient.interceptors.response.use(
      (response) => {
        // Return successful responses as-is
        return response;
      },
      (error) => {
        console.log("APIService Interceptor caught an error:", error);
        // Check if the error is a 401 Unauthorized
        if (error.response && error.response.status === 401) {
          // Handle logout
          handleUserLogout();
        }
        // Show 403 message for GET requests only — mutations are handled by useMutationWithToast
        if (error.response && error.response.status === 403) {
          const method = error.config?.method?.toLowerCase();
          if (method === "get") {
            const msg =
              error.response.data?.detail ||
              "You don't have permission to do that.";
            toast.error(msg);
          }
        }
        if (error.response && error.response.status >= 500) {
          // Handle logout
          toast.error("Server error. Please try again later.");
        } else if (error.request) {
          // No response received from server (e.g., backend is down)
          // toast.error(
          //   "No response from server. Please check your network connection or try again later."
          // );
        } else {
          // Something went wrong in setting up the request
          return Promise.reject({
            message: "Unexpected error occurred. Please try again.",
            code: "UNKNOWN_ERROR",
            original: error,
          });
        }

        // Re-throw the error so it can still be handled by the calling code if needed
        return Promise.reject(error);
      },
    );
  }

  static async get<T = unknown, R = AxiosResponse<T>, D = unknown>(
    url: string,
    options?: APIServiceOptions,
  ) {
    const headers = {} as AxiosRequestHeaders;

    if (options?.withAuthToken) {
      headers.Authorization = getAccessToken() as string;
    }
    this.addLocationHeader(headers);

    return this.APIClient.get<T, R, D>(url, {
      headers,
      params: options?.params,
      timeout: options?.timeout,
    });
  }

  static async post<T = unknown, R = AxiosResponse<T>, D = unknown>(
    url: string,
    data?: D,
    options?: APIServiceOptions,
  ) {
    const headers = {} as AxiosRequestHeaders;

    if (options?.withAuthToken) {
      headers.Authorization = getAccessToken() as string;
    }
    this.addLocationHeader(headers);

    if (options?.formData) {
      headers["Content-Type"] = "multipart/form-data";
    }

    return this.APIClient.post<T, R, D>(url, data, {
      headers,
      params: options?.params,
      ...(options?.formData ? { transformRequest: () => data } : {}),
    });
  }

  static async put<T = unknown, R = AxiosResponse<T>, D = unknown>(
    url: string,
    data?: D,
    options?: APIServiceOptions,
  ) {
    const headers = {} as AxiosRequestHeaders;

    if (options?.withAuthToken) {
      headers.Authorization = getAccessToken() as string;
    }
    this.addLocationHeader(headers);

    if (options?.formData) {
      headers["Content-Type"] = "multipart/form-data";
    }

    return this.APIClient.put<T, R, D>(url, data, {
      headers,
      params: options?.params,
      ...(options?.formData ? { transformRequest: () => data } : {}),
    });
  }

  static async patch<T = unknown, R = AxiosResponse<T>, D = unknown>(
    url: string,
    data?: D,
    options?: APIServiceOptions,
  ) {
    const headers = {} as AxiosRequestHeaders;

    if (options?.withAuthToken) {
      headers.Authorization = getAccessToken() as string;
    }
    this.addLocationHeader(headers);

    return this.APIClient.patch<T, R, D>(url, data, {
      headers,
      params: options?.params,
      ...(options?.preservePayloadKeys
        ? {
            // Preserve arbitrary user-supplied keys (for example Excel
            // headers), while still letting Axios perform its normal JSON
            // serialization exactly once.
            transformRequest: [
              (request: D) => request,
              ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
            ],
          }
        : {}),
    });
  }

  static async delete<T = unknown, R = AxiosResponse<T>, D = unknown>(
    url: string,
    options?: APIServiceOptions,
  ) {
    const headers = {} as AxiosRequestHeaders;

    if (options?.withAuthToken) {
      headers.Authorization = getAccessToken() as string;
    }
    this.addLocationHeader(headers);

    return this.APIClient.delete<T, R, D>(url, {
      headers,
      params: options?.params,
    });
  }
}
